import { useState } from 'react';
import { Stack, Group, Button, Text } from '@repletoryreach/sdk-ui';
import { TextInput, NumberInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useCreateSkuRate, usePriceLadder } from '../../../../data/useSkuRates';
import { formatMinor } from '../../../../lib/format';
import { floorMinor, ladderRefusal } from '../../../../lib/priceLadder';
import { useT } from '../../../../i18n/I18nProvider';
import { marginText } from './RateVersionModal';

const UNITS = ['message', '1k_tokens', '10k_tokens', 'document', 'question', 'file', 'gb_day', 'call', 'minute', 'number', 'month', 'session', 'code', '1k_chars', 'image'] as const;
const CATEGORIES = ['whatsapp', 'sms', 'email', 'ussd', 'ai', 'knowledge', 'notifications', 'media', 'web', 'meta', 'voice'] as const;
const CURRENCIES = ['ZAR', 'NGN', 'KES', 'USD', 'EUR', 'GBP'] as const;

/**
 * A new SKU, or a new currency for an existing one: a NGN price for a
 * Nigerian marketing message, a SKU for a service that starts being metered.
 * Cost and charge are entered side by side so the margin is a decision, not
 * a discovery. The charge has to clear the floor for the cost typed (the cost
 * plus the free plan's markup), exactly as a changed rate does.
 */
export function NewSkuRateForm({ opened, onClose, currencies }: { opened: boolean; onClose: () => void; currencies: string[] }) {
  const t = useT();
  const mut = useCreateSkuRate();
  const ladder = usePriceLadder();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<string>('whatsapp');
  const [unit, setUnit] = useState<string>('message');
  const [currency, setCurrency] = useState<string>('ZAR');
  const [cost, setCost] = useState<number | ''>('');
  const [charge, setCharge] = useState<number | ''>('');
  const [refusal, setRefusal] = useState<string | null>(null);

  // Entered in major units (0.87), stored in minor units (87).
  const chargeMinor = typeof charge === 'number' ? Math.round(charge * 100) : 0;
  const previewCost = typeof cost === 'number' ? Math.round(cost * 100) : 0;
  // Null until the plans have been read. The service checks the floor either way.
  const entryMarkup = ladder.entryMarkupPercent;
  const floor = entryMarkup == null ? null : floorMinor(previewCost, entryMarkup);
  const belowFloor = floor != null && typeof charge === 'number' && chargeMinor < floor;
  const valid = code.trim().length >= 3 && name.trim().length > 0 && typeof charge === 'number' && charge >= 0 && !belowFloor;

  async function submit() {
    if (!valid || typeof charge !== 'number') return;
    setRefusal(null);
    try {
      await mut.mutateAsync({ sku_code: code.trim().toLowerCase(), name: name.trim(), amount_minor: chargeMinor, cost_minor: previewCost, unit, category, currency });
      notifySuccess(t('billing.skuRates.new.created', { sku: code.trim().toLowerCase(), charge: formatMinor(chargeMinor, currency) }), t('billing.skuRates.new.createdTitle'));
      setCode(''); setName(''); setCost(''); setCharge('');
      onClose();
    } catch (e) {
      const refused = ladderRefusal(e);
      if (refused?.code === 'RATE_BELOW_FLOOR' && refused.floor_minor != null) {
        setRefusal(t('billing.skuRates.new.refusal.belowFloor', {
          cost: formatMinor(refused.cost_minor ?? previewCost, currency),
          floor: formatMinor(refused.floor_minor, currency),
          pct: refused.entry_markup_percent ?? entryMarkup ?? ''
        }));
        // The service's floor is not the one shown, so the free plan's markup has moved since it was read.
        void ladder.refetch();
        return;
      }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.skuRates.new.failed'));
    }
  }

  const chargeHint = floor != null && floor > 0 && entryMarkup != null
    ? t('billing.skuRates.edit.floorHint', { floor: formatMinor(floor, currency), pct: entryMarkup })
    : t('billing.skuRates.edit.amountHint', { example: formatMinor(87, currency) });
  const chargeError = refusal
    ?? (belowFloor && floor != null ? t('billing.skuRates.new.belowFloor', { charge: formatMinor(chargeMinor, currency), floor: formatMinor(floor, currency) }) : undefined);

  const currencyOptions = Array.from(new Set([...CURRENCIES, ...currencies])).map((c) => ({ value: c, label: c }));

  return (
    <Modal opened={opened} onClose={onClose} title={t('billing.skuRates.new.title')} size="md">
      <Stack gap="md">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.skuRates.new.body')}</Text>
        <TextInput label={t('billing.skuRates.new.code')} value={code} onChange={(e) => setCode(e.currentTarget.value)} placeholder="wa.marketing.ng" description={t('billing.skuRates.new.codeHint')} />
        <TextInput label={t('common.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} placeholder={t('billing.skuRates.new.namePlaceholder')} description={t('billing.skuRates.new.nameHint')} />
        <Group gap="sm" grow>
          <Select label={t('billing.skuRates.new.category')} value={category} onChange={(e) => setCategory(e.currentTarget.value)}
            data={CATEGORIES.map((c) => ({ value: c, label: t(`billing.skuRates.category.${c}`) }))} />
          <Select label={t('billing.skuRates.new.unit')} value={unit} onChange={(e) => setUnit(e.currentTarget.value)}
            data={UNITS.map((u) => ({ value: u, label: t(`billing.skuRates.unit.${u}`) }))} />
          <Select label={t('common.currency')} value={currency} onChange={(e) => setCurrency(e.currentTarget.value)} data={currencyOptions} />
        </Group>
        <Group gap="sm" grow align="flex-start">
          <NumberInput label={t('billing.skuRates.edit.costLabel', { currency })} value={cost} onChange={(v) => { setCost(typeof v === 'number' ? v : ''); setRefusal(null); }} min={0} step={0.01} description={t('billing.skuRates.edit.costHint')} />
          <NumberInput label={t('billing.skuRates.edit.amountLabel', { currency })} value={charge} onChange={(v) => { setCharge(typeof v === 'number' ? v : ''); setRefusal(null); }} min={0} step={0.01} description={chargeHint} error={chargeError} />
        </Group>
        {typeof charge === 'number' && (
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('billing.skuRates.edit.preview', { charge: formatMinor(chargeMinor, currency), cost: formatMinor(previewCost, currency), margin: marginText(chargeMinor, previewCost, currency) })}
          </Text>
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={mut.isPending} disabled={!valid} onClick={() => void submit()}>{t('billing.skuRates.new.submit')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
